/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface AdminExpense {
    id: string;
    company_id: string;
    entity_id?: string;
    expense_date: string;
    amount: number;
    category: string;
    description?: string;
    created_at: string;
    company_entities?: { name: string };
}

const currencyFormatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
});

const compactCurrencyFormatter = (value: number) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
    return `₹${value}`;
};

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280'];

// CSR Calculator helper
type YearlyData = { pbt: string; netProfit198: string; adjustedAmount: string };
type CalculatorState = { reason: string; financial_year: string; years: { [key: string]: YearlyData }; setOffClaimed: string; yearLabels: string[]; };

const computeExcelCSR = (calcData: CalculatorState) => {
    const parse = (v: any) => parseFloat(v) || 0;
    const calcYear = (y: YearlyData) => parse(y.netProfit198) - parse(y.adjustedAmount);

    const yearKeys = Object.keys(calcData.years);
    const tnp = yearKeys.map(k => calcYear(calcData.years[k]));
    const averageProfit = tnp.reduce((a, b) => a + b, 0) / 3;
    const csrCurrentYear = Math.max(0, averageProfit * 0.02);
    const setOff = parse(calcData.setOffClaimed);
    const finalObligation = Math.max(0, csrCurrentYear - setOff);

    // If input numbers are entered in Crores (< 10000), 1 Crore = 10,000,000 INR
    const isCroreScale = averageProfit > 0 && averageProfit < 10000;
    const scaleFactor = isCroreScale ? 10000000 : 1;

    const averageProfitRupees = averageProfit * scaleFactor;
    const csrCurrentYearRupees = csrCurrentYear * scaleFactor;
    const finalObligationRupees = finalObligation * scaleFactor;
    const projectAmountRupees = finalObligationRupees * 0.95;
    const adminAmountRupees = finalObligationRupees * 0.05;

    return { 
        tnp, 
        averageProfit, 
        csrCurrentYear, 
        finalObligation, 
        projectAmount: finalObligation * 0.95, 
        adminAmount: finalObligation * 0.05, 
        isApplicable: finalObligation > 0,
        isCroreScale,
        averageProfitRupees,
        csrCurrentYearRupees,
        finalObligationRupees,
        projectAmountRupees,
        adminAmountRupees
    };
};

const getPrecedingYears = (fyStr: string) => {
    const match = fyStr.match(/FY (\d{4})-\d{2}/);
    if (!match) return ['Year 1', 'Year 2', 'Year 3'];
    const startYear = parseInt(match[1]);
    return [
        `${startYear - 1}-${(startYear).toString().slice(-2)}`,
        `${startYear - 2}-${(startYear - 1).toString().slice(-2)}`,
        `${startYear - 3}-${(startYear - 2).toString().slice(-2)}`
    ];
};

// Generate financial years
const generateFinancialYears = () => {
    const years: string[] = [];
    const currentYear = new Date().getFullYear();
    for (let year = 2021; year <= 2050; year++) {
        years.push(`FY ${year}-${(year + 1).toString().slice(-2)}`);
    }
    return years;
};
const FINANCIAL_YEARS = generateFinancialYears();
const getCurrentFY = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const fyStart = currentMonth >= 3 ? currentYear : currentYear - 1;
    return `FY ${fyStart}-${(fyStart + 1).toString().slice(-2)}`;
};

const defaultCalcState = (): CalculatorState => ({
    reason: 'Net profit of Rs. 5 crore or more',
    financial_year: getCurrentFY(),
    years: { '0': { pbt: '', netProfit198: '', adjustedAmount: '' }, '1': { pbt: '', netProfit198: '', adjustedAmount: '' }, '2': { pbt: '', netProfit198: '', adjustedAmount: '' } },
    setOffClaimed: '',
    yearLabels: getPrecedingYears(getCurrentFY())
});

const CorporateFinancials: React.FC = () => {
    const { userProfile } = useAuth();
    const companyId = userProfile?.company_id;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [companyProfile, setCompanyProfile] = useState<any>(null);
    const [companyEntities, setCompanyEntities] = useState<any[]>([]);
    const [foundationContributions, setFoundationContributions] = useState<any[]>([]);
    const [adminExpenses, setAdminExpenses] = useState<AdminExpense[]>([]);
    const [companyCsrMandate, setCompanyCsrMandate] = useState<number | null>(null);
    const [primaryAdminBudgetInput, setPrimaryAdminBudgetInput] = useState<string>('');
    const [projectBudgetInput, setProjectBudgetInput] = useState<string>('');

    const [newAdminExpense, setNewAdminExpense] = useState({ date: new Date().toISOString().split('T')[0], amount: '', category: 'Travel', description: '', entity_id: '' });
    const [isSavingAdminExpense, setIsSavingAdminExpense] = useState(false);
    const [newContribution, setNewContribution] = useState({ date: new Date().toISOString().split('T')[0], amount: '', entity_id: '', notes: '' });
    const [isSavingContribution, setIsSavingContribution] = useState(false);
    const [csrNotification, setCsrNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [newEntity, setNewEntity] = useState({ name: '', csr_mandate: '', admin_budget: '', primary_account_budget: '', entity_budget: '', financial_year: getCurrentFY(), net_worth_crore: '', turnover_crore: '', net_profit_crore: '', is_escrow: false });

    const [entityLogo, setEntityLogo] = useState<File | null>(null);
    const [entitySaving, setEntitySaving] = useState(false);
    const [editingEntityId, setEditingEntityId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'calculator' | 'entities' | 'escrow' | 'admin' | 'transfers' | 'disbursements'>('calculator');

    // CSR Calculator state
    const [singleCalc, setSingleCalc] = useState<CalculatorState>(defaultCalcState());
    const [entityCalc, setEntityCalc] = useState<CalculatorState>(defaultCalcState());
    const [savingCalc, setSavingCalc] = useState(false);
    const [foundationSaving, setFoundationSaving] = useState(false);
    const [editingContributionId, setEditingContributionId] = useState<string | null>(null);
    const [editingDisbursementId, setEditingDisbursementId] = useState<string | null>(null);
    const [signedLogoUrls, setSignedLogoUrls] = useState<Record<string, string>>({});
    const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});

    // Direct Expenditure State
    const [showDirectExpForm, setShowDirectExpForm] = useState(false);
    const [isSavingDirectExp, setIsSavingDirectExp] = useState(false);
    const [directExpData, setDirectExpData] = useState({ project_id: '', entity_id: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });

    // Parity with CompanyFinance state
    const [projects, setProjects] = useState<any[]>([]);
    const [fundRequests, setFundRequests] = useState<any[]>([]);
    const [disbursements, setDisbursements] = useState<any[]>([]);

    // Helper to obtain logo preview URL
    const getLogoPreviewUrl = () => {
        if (entityLogo) {
            return URL.createObjectURL(entityLogo);
        }
        if (editingEntityId) {
            const entity = companyEntities.find(e => e.id === editingEntityId);
            if (entity?.logo_url) {
                return signedLogoUrls[entity.logo_url] || entity.logo_url;
            }
        }
        return null;
    };

    const fetchData = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const { data: companyData, error: companyError } = await supabase
                .from('companies')
                .select('*')
                .eq('id', companyId)
                .single();

            if (companyError) throw companyError;
            setCompanyProfile(companyData);
            setCompanyCsrMandate(companyData?.csr_mandate ?? null);
            setPrimaryAdminBudgetInput(companyData?.admin_budget?.toString() || '0');
            const projectVal = (companyData?.csr_mandate || 0) - (companyData?.admin_budget || 0);
            setProjectBudgetInput(projectVal.toString());

            // Pre-fill skipped until DB columns support 3 years.

            const [entitiesRes, contributionsRes, adminExpensesRes, projectsRes] = await Promise.all([
                supabase.from('company_entities').select('*').eq('company_id', companyId).order('created_at', { ascending: true }),
                supabase.from('foundation_contributions').select('*, company_entities(name)').eq('company_id', companyId),
                supabase.from('company_admin_expenses').select('*').eq('company_id', companyId).order('expense_date', { ascending: false }),
                supabase.from('projects').select('*').eq('company_id', companyId).order('created_at', { ascending: false })
            ]);

            if (entitiesRes.error) throw entitiesRes.error;

            const entities = entitiesRes.data || [];
            setCompanyEntities(entities);
            if (companyData?.setup_type === 'single' && entities.filter((e: any) => !e.is_escrow).length === 0 && companyData?.csr_mandate !== undefined && companyData?.csr_mandate !== null) {
                setNewEntity(prev => ({
                    ...prev,
                    csr_mandate: companyData?.csr_mandate !== null && companyData?.csr_mandate !== undefined ? companyData.csr_mandate.toString() : '',
                    admin_budget: companyData?.admin_budget !== null && companyData?.admin_budget !== undefined ? companyData.admin_budget.toString() : '',
                    entity_budget: ((Number(companyData?.csr_mandate) || 0) - (Number(companyData?.admin_budget) || 0)).toString()
                }));
            }
            setFoundationContributions(contributionsRes.data || []);
            setProjects(projectsRes.data || []);

            const projectIds = (projectsRes.data || []).map((p: any) => p.id);
            if (projectIds.length > 0) {
                const [requestsRes, disbursementsRes] = await Promise.all([
                    supabase.from('fund_requests').select('*, projects(name), ngos(legal_name)').in('project_id', projectIds).order('created_at', { ascending: false }),
                    supabase.from('fund_disbursements').select('*, projects(name, implementation_type), ngos(legal_name), company_entities(name)').in('project_id', projectIds).order('released_at', { ascending: false })
                ]);

                const mappedRequests = (requestsRes.data || []).map((req: any) => ({
                    ...req,
                    projects: Array.isArray(req.projects) && req.projects.length > 0 ? req.projects[0] : (req.projects || { name: 'Project' }),
                    ngos: Array.isArray(req.ngos) && req.ngos.length > 0 ? req.ngos[0] : (req.ngos || { legal_name: 'NGO' })
                }));
                setFundRequests(mappedRequests);

                const mappedDisbursements = (disbursementsRes.data || []).map((disb: any) => ({
                    ...disb,
                    projects: Array.isArray(disb.projects) && disb.projects.length > 0 ? disb.projects[0] : (disb.projects || { name: 'Project' }),
                    ngos: Array.isArray(disb.ngos) && disb.ngos.length > 0 ? disb.ngos[0] : (disb.ngos || { legal_name: 'Direct Implementation' }),
                    company_entities: Array.isArray(disb.company_entities) && disb.company_entities.length > 0 ? disb.company_entities[0] : (disb.company_entities || null)
                }));
                // Filter to show ONLY Direct Implementation records (where no NGO is involved)
                const directDisbursements = mappedDisbursements.filter(d =>
                    d.ngo_id === null
                );
                setDisbursements(directDisbursements);
            }

            const mappedAdminExpenses = (adminExpensesRes.data || []).map((exp: any) => {
                const entityMatch = entities.find((e: any) => e.id === exp.entity_id);
                return {
                    ...exp,
                    company_entities: entityMatch ? { name: entityMatch.name } : undefined
                };
            });
            setAdminExpenses(mappedAdminExpenses);

        } catch (err: any) {
            console.error('Error fetching data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchSignedLogos = async () => {
            const urlsToSign = companyEntities.map(e => e.logo_url).filter(Boolean);
            const newSignedUrls: Record<string, string> = { ...signedLogoUrls };
            let updated = false;

            for (const url of urlsToSign) {
                if (newSignedUrls[url]) continue;
                try {
                    let path = url;
                    if (url.includes('/object/public/company-documents/')) {
                        path = url.split('/object/public/company-documents/')[1].split('?')[0];
                    } else if (url.includes('/company-documents/')) {
                        path = url.split('/company-documents/')[1].split('?')[0];
                    }

                    if (path) {
                        const { data } = await supabase.storage.from('company-documents').createSignedUrl(decodeURIComponent(path), 3600);
                        if (data?.signedUrl) {
                            newSignedUrls[url] = data.signedUrl;
                            updated = true;
                        }
                    }
                } catch (err) {
                    console.error('Failed to sign logo URL:', err);
                }
            }

            if (updated) {
                setSignedLogoUrls(newSignedUrls);
            }
        };

        if (companyEntities.length > 0) {
            fetchSignedLogos();
        }
    }, [companyEntities]);

    const financeSnapshot = React.useMemo(() => {
        const summaryBars = [
            { label: 'Allocated Budget', value: projects.reduce((sum, p) => sum + (p.budget || 0), 0), color: '#0f766e' },
            { label: 'Released Funds', value: disbursements.reduce((sum, d) => sum + (d.amount || 0), 0) + adminExpenses.reduce((sum, e) => sum + (e.amount || 0), 0), color: '#3b82f6' },
            { label: 'Admin Expenses', value: adminExpenses.reduce((sum, e) => sum + (e.amount || 0), 0), color: '#10b981' }
        ];

        const disbursementMap = new Map<string, { label: string; value: number; sortValue: number }>();
        disbursements.forEach(disbursement => {
            const date = disbursement.released_at ? new Date(disbursement.released_at) : new Date();
            const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
            const key = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
            const label = `${monthStart.toLocaleString('en-US', { month: 'short' })} '${monthStart.getFullYear().toString().slice(-2)}`;
            const current = disbursementMap.get(key) || { label, value: 0, sortValue: monthStart.getTime() };
            current.value += disbursement.amount || 0;
            disbursementMap.set(key, current);
        });

        const disbursementTrend = Array.from(disbursementMap.values())
            .sort((a, b) => a.sortValue - b.sortValue)
            .slice(-6);

        if (disbursementTrend.length === 0) {
            disbursementTrend.push({ label: 'No Data', value: 0, sortValue: 0 });
        }

        return {
            summaryBars,
            disbursementTrend,
            entityBreakdownData: companyEntities.map(e => ({
                name: e.name,
                mandate: e.csr_mandate || 0
            }))
        };
    }, [projects, disbursements, adminExpenses, companyEntities]);

    const handleEditClick = (entity: any) => {
        setNewEntity({
            name: entity.name,
            csr_mandate: entity.csr_mandate?.toString() || '',
            admin_budget: entity.admin_budget?.toString() || '',
            primary_account_budget: entity.primary_account_budget?.toString() || '',
            entity_budget: entity.entity_budget?.toString() || '',
            financial_year: getCurrentFY(),
            net_worth_crore: '',
            turnover_crore: '',
            net_profit_crore: '',
            is_escrow: entity.is_escrow || false
        });
        setEditingEntityId(entity.id);
        setEntityCalc(defaultCalcState());
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setNewEntity({ name: '', csr_mandate: '', admin_budget: '', primary_account_budget: '', entity_budget: '', financial_year: getCurrentFY(), net_worth_crore: '', turnover_crore: '', net_profit_crore: '', is_escrow: false });
        setEditingEntityId(null);
        setEntityLogo(null);
        setEntityCalc(defaultCalcState());
    };

    const handleSaveEntity = async (e: React.FormEvent) => {
        e.preventDefault();
        if (companyProfile?.setup_type !== 'single' && !newEntity.name.trim()) return;

        const existingEntity = editingEntityId ? companyEntities.find(e => e.id === editingEntityId) : null;
        if (companyProfile?.setup_type !== 'single' && !entityLogo && (!existingEntity || !existingEntity.logo_url)) {
            setError('Company Logo is mandatory.');
            setTimeout(() => setError(null), 4000);
            return;
        }

        setEntitySaving(true);
        try {
            let logoUrl = null;
            if (entityLogo) {
                const fileExt = entityLogo.name.split('.').pop();
                const path = `${companyId}/entities/${Math.random()}.${fileExt}`;
                const { error: uploadError } = await supabase.storage.from('company-documents').upload(path, entityLogo);
                if (uploadError) throw uploadError;
                logoUrl = supabase.storage.from('company-documents').getPublicUrl(path).data.publicUrl;
            }

            const calcRes = computeExcelCSR(entityCalc);
            let csrMandateNum = Number(newEntity.csr_mandate) || 0;
            let adminBudgetNum = Number(newEntity.admin_budget) || 0;
            let entityBudgetNum = Number(newEntity.entity_budget) || 0;
            let primaryAccountBudgetNum = Number(newEntity.primary_account_budget) || 0;

            // If saving from CSR Calculator tab and calcRes.finalObligation > 0, auto-populate calculated numbers in INR Rupees
            if (activeTab === 'calculator' && calcRes.finalObligation > 0) {
                csrMandateNum = Math.round(calcRes.finalObligationRupees);
                adminBudgetNum = Math.round(calcRes.adminAmountRupees);
                entityBudgetNum = Math.round(calcRes.projectAmountRupees);
            } else if (!csrMandateNum && (adminBudgetNum || primaryAccountBudgetNum || entityBudgetNum)) {
                csrMandateNum = adminBudgetNum + primaryAccountBudgetNum + entityBudgetNum;
            }

            const entityData: any = {
                name: companyProfile?.setup_type === 'single' ? (companyProfile?.name || companyProfile?.company_name || 'Single Company') : newEntity.name.trim(),
                financial_year: newEntity.financial_year || getCurrentFY(),
                net_worth_crore: Number(newEntity.net_worth_crore) || 0,
                turnover_crore: Number(newEntity.turnover_crore) || 0,
                net_profit_crore: Number(newEntity.net_profit_crore) || 0,
                csr_mandate: csrMandateNum,
                admin_budget: adminBudgetNum,
                primary_account_budget: primaryAccountBudgetNum,
                entity_budget: entityBudgetNum,
                is_escrow: newEntity.is_escrow,
            };
            if (logoUrl) {
                entityData.logo_url = logoUrl;
            } else if (companyProfile?.setup_type === 'single' && companyProfile?.logo_url && !existingEntity?.logo_url) {
                entityData.logo_url = companyProfile.logo_url;
            }

            if (editingEntityId) {
                let { data, error } = await supabase.from('company_entities').update(entityData).eq('id', editingEntityId).select().single();
                if (error && (error.message.includes('column') || error.message.includes('schema cache'))) {
                    const fallbackData = { ...entityData };
                    delete fallbackData.financial_year;
                    delete fallbackData.net_worth_crore;
                    delete fallbackData.turnover_crore;
                    delete fallbackData.net_profit_crore;
                    const res = await supabase.from('company_entities').update(fallbackData).eq('id', editingEntityId).select().single();
                    data = res.data;
                    error = res.error;
                }
                if (error) throw error;
                // Move updated entity to the bottom as per user request
                setCompanyEntities(prev => [...prev.filter(e => e.id !== editingEntityId), data]);
                setCsrNotification({ 
                    type: 'success', 
                    message: `✓ Group company '${data.name}' updated! Total Mandate: ${currencyFormatter.format(csrMandateNum)}` 
                });
            } else {
                entityData.company_id = companyId;
                let { data, error } = await supabase.from('company_entities').insert(entityData).select().single();
                if (error && (error.message.includes('column') || error.message.includes('schema cache'))) {
                    const fallbackData = { ...entityData };
                    delete fallbackData.financial_year;
                    delete fallbackData.net_worth_crore;
                    delete fallbackData.turnover_crore;
                    delete fallbackData.net_profit_crore;
                    const res = await supabase.from('company_entities').insert(fallbackData).select().single();
                    data = res.data;
                    error = res.error;
                }
                if (error) throw error;
                setCompanyEntities(prev => [...prev, data]);
                setCsrNotification({ 
                    type: 'success', 
                    message: activeTab === 'calculator' 
                        ? `✓ '${data.name}' saved with CSR Mandate of ${currencyFormatter.format(csrMandateNum)}! Updated in Group Companies tab.` 
                        : `${companyProfile?.setup_type === 'single' ? 'Budget' : 'Company'} added successfully` 
                });
            }
            cancelEdit();
            setTimeout(() => setCsrNotification(null), 4000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setEntitySaving(false);
        }
    };

    const handleDeleteEntity = async (id: string) => {
        if (!window.confirm('Are you sure? This will delete all connected data.')) return;
        try {
            const { error } = await supabase.from('company_entities').delete().eq('id', id);
            if (error) throw error;
            setCompanyEntities(prev => prev.filter(e => e.id !== id));
            setCsrNotification({ type: 'success', message: 'Company deleted successfully' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleSaveFoundationMandate = async (e: React.FormEvent) => {
        e.preventDefault();
        setFoundationSaving(true);
        try {
            const isSingle = companyProfile?.setup_type === 'single';
            // In single mode, we might have edited admin/project, so we take them as source of truth for saving
            const adminValue = Number(primaryAdminBudgetInput) || 0;
            const projectValue = Number(projectBudgetInput) || 0;
            const amountValue = isSingle ? (adminValue + projectValue) : companyEntities.reduce((sum, e) => sum + (Number(e.primary_account_budget) || 0), 0);

            const updateData: any = { csr_mandate: amountValue, admin_budget: adminValue };

            const { error } = await supabase.from('companies').update(updateData).eq('id', companyId);
            if (error) throw error;
            setCompanyCsrMandate(amountValue);
            setCsrNotification({ type: 'success', message: 'Budget updated successfully' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setFoundationSaving(false);
        }
    };

    const handleTotalMandateChange = (val: string) => {
        const total = Number(val) || 0;
        setCompanyCsrMandate(total);
        const admin = Math.round(total * 0.05);
        const project = total - admin;
        setPrimaryAdminBudgetInput(admin.toString());
        setProjectBudgetInput(project.toString());
    };

    const handleAdminBudgetChange = (val: string) => {
        setPrimaryAdminBudgetInput(val);
        const total = companyCsrMandate || 0;
        const admin = Number(val) || 0;
        setProjectBudgetInput((total - admin).toString());
    };

    const handleProjectBudgetChange = (val: string) => {
        setProjectBudgetInput(val);
        const total = companyCsrMandate || 0;
        const project = Number(val) || 0;
        setPrimaryAdminBudgetInput((total - project).toString());
    };

    const handleSaveAdminExpense = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newAdminExpense.amount) return;
        setIsSavingAdminExpense(true);
        try {
            const { data, error } = await supabase.from('company_admin_expenses').insert({
                company_id: companyId,
                entity_id: newAdminExpense.entity_id || null,
                expense_date: newAdminExpense.date,
                amount: Number(newAdminExpense.amount),
                category: newAdminExpense.category,
                description: newAdminExpense.description
            }).select().single();
            if (error) throw error;

            const entityMatch = companyEntities.find(e => e.id === data.entity_id);
            setAdminExpenses(prev => [{ ...data, company_entities: entityMatch ? { name: entityMatch.name } : undefined }, ...prev]);
            setNewAdminExpense({ date: new Date().toISOString().split('T')[0], amount: '', category: 'Travel', description: '', entity_id: '' });
            setCsrNotification({ type: 'success', message: 'Expenditure recorded successfully' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSavingAdminExpense(false);
        }
    };

    const handleDeleteAdminExpense = async (id: string) => {
        if (!window.confirm('Delete this expense?')) return;
        try {
            const { error } = await supabase.from('company_admin_expenses').delete().eq('id', id);
            if (error) throw error;
            setAdminExpenses(prev => prev.filter(e => e.id !== id));
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleSaveFoundationContribution = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newContribution.amount) return;
        setIsSavingContribution(true);
        try {
            const contributionPayload = {
                company_id: companyId,
                entity_id: newContribution.entity_id || null,
                amount: parseFloat(newContribution.amount),
                contribution_date: newContribution.date,
                notes: newContribution.notes
            };

            if (editingContributionId) {
                const { data, error } = await supabase
                    .from('foundation_contributions')
                    .update(contributionPayload)
                    .eq('id', editingContributionId)
                    .select('*, company_entities(name)')
                    .single();
                if (error) throw error;
                setFoundationContributions(prev => prev.map(c => c.id === editingContributionId ? data : c));
                setEditingContributionId(null);
                setCsrNotification({ type: 'success', message: 'Transfer updated successfully' });
            } else {
                const { data, error } = await supabase
                    .from('foundation_contributions')
                    .insert([contributionPayload])
                    .select('*, company_entities(name)')
                    .single();
                if (error) throw error;
                setFoundationContributions(prev => [data, ...prev]);
                setCsrNotification({ type: 'success', message: `${companyProfile?.setup_type === 'single' ? 'Contribution' : 'Transfer'} recorded successfully` });
            }

            setNewContribution({ date: new Date().toISOString().split('T')[0], amount: '', entity_id: '', notes: '' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSavingContribution(false);
        }
    };

    const handleEditContribution = (contribution: any) => {
        setNewContribution({
            date: contribution.contribution_date,
            amount: contribution.amount.toString(),
            entity_id: contribution.entity_id || '',
            notes: contribution.notes || ''
        });
        setEditingContributionId(contribution.id);
        // Scroll to the form
        const element = document.getElementById('record-transfer-form');
        if (element) element.scrollIntoView({ behavior: 'smooth' });
    };

    const handleDeleteContribution = async (id: string) => {
        if (!window.confirm('Delete this transfer record?')) return;
        try {
            const { error } = await supabase.from('foundation_contributions').delete().eq('id', id);
            if (error) throw error;
            setFoundationContributions(prev => prev.filter(c => c.id !== id));
            setCsrNotification({ type: 'success', message: 'Transfer removed successfully' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleSaveDirectExpenditure = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!directExpData.project_id || !directExpData.amount) return;
        setIsSavingDirectExp(true);
        try {
            const expenditurePayload = {
                project_id: directExpData.project_id,
                entity_id: directExpData.entity_id || null,
                amount: parseFloat(directExpData.amount),
                released_at: directExpData.date,
                notes: directExpData.notes,
                routing_method: 'direct_to_project'
            };

            if (editingDisbursementId) {
                const { data, error } = await supabase
                    .from('fund_disbursements')
                    .update(expenditurePayload)
                    .eq('id', editingDisbursementId)
                    .select('*, projects(name, implementation_type), ngos(legal_name), company_entities(name)')
                    .single();
                if (error) throw error;
                setDisbursements(prev => prev.map(d => d.id === editingDisbursementId ? data : d));
                setEditingDisbursementId(null);
                setCsrNotification({ type: 'success', message: 'Expenditure updated successfully' });
            } else {
                const { data, error } = await supabase
                    .from('fund_disbursements')
                    .insert([expenditurePayload])
                    .select('*, projects(name, implementation_type), ngos(legal_name), company_entities(name)')
                    .single();
                if (error) throw error;
                setDisbursements(prev => [data, ...prev]);
                setCsrNotification({ type: 'success', message: 'Expenditure recorded successfully' });
            }

            setShowDirectExpForm(false);
            setDirectExpData({ project_id: '', entity_id: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSavingDirectExp(false);
        }
    };

    const handleEditDisbursement = (disb: any) => {
        setDirectExpData({
            project_id: disb.project_id,
            entity_id: disb.entity_id || '',
            amount: disb.amount.toString(),
            date: disb.released_at,
            notes: disb.notes || ''
        });
        setEditingDisbursementId(disb.id);
        setShowDirectExpForm(true);
        // Scroll to the form
        window.scrollTo({ top: 300, behavior: 'smooth' });
    };

    if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

    const stats = {
        totalProjects: projects.length,
        totalMandate: companyProfile?.setup_type === 'single' ? (companyCsrMandate || 0) : companyEntities.reduce((sum, e) => sum + (e.csr_mandate || 0), 0),
        totalDisbursed: (disbursements.reduce((sum, d) => sum + (d.amount || 0), 0)) + (adminExpenses.reduce((sum, e) => sum + (e.amount || 0), 0)),
        pendingRequests: fundRequests.filter(r => r.status === 'pending').length,
        adminExpenses: adminExpenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    };

    const handleDeleteDisbursement = async (id: string) => {
        if (!window.confirm('Delete this record?')) return;
        try {
            const { error } = await supabase.from('fund_disbursements').delete().eq('id', id);
            if (error) throw error;
            setDisbursements(prev => prev.filter(d => d.id !== id));
            setCsrNotification({ type: 'success', message: 'Record removed successfully' });
            setTimeout(() => setCsrNotification(null), 3000);
        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <div style={{ padding: '2rem' }}>
            <h1 style={{ marginBottom: '2rem' }}>Corporate Financial Management</h1>

            {error && <div className="toast toast-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {csrNotification && <div className={`toast toast-${csrNotification.type}`} style={{ marginBottom: '1rem' }}>{csrNotification.message}</div>}

            <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '2.5rem', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                {(['calculator', 'entities', 'escrow', 'admin', 'transfers', 'disbursements'] as const)
                    .filter(tab => {
                        if (companyProfile?.setup_type === 'single') {
                            return tab !== 'transfers';
                        }
                        return true;
                    })
                    .map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '0.6rem 1.1rem',
                                border: 'none',
                                background: 'transparent',
                                borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
                                color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
                                fontWeight: activeTab === tab ? '600' : '450',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                                marginBottom: '-1px',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {tab === 'calculator' ? '📊 CSR Calculator' :
                            tab === 'entities' ? (companyProfile?.setup_type === 'single' ? 'Company Finance' : 'Group Entities & Budgets') :
                            tab === 'escrow' ? 'Escrow Accounts' :
                                tab === 'admin' ? 'Admin Expenses' :
                                    tab === 'transfers' ? (companyProfile?.setup_type === 'single' ? 'Transfers' : 'Foundation Transfers') :
                                        tab === 'disbursements' ? 'Fund Releases' : tab}
                        </button>
                    ))}
            </div>

            <div style={{ display: 'grid', gap: '2rem' }}>
                {/* ══════════════════════ CSR CALCULATOR TAB ══════════════════════ */}
                {activeTab === 'calculator' && (
                    <div style={{ display: 'grid', gap: '2rem' }}>
                        {companyProfile?.setup_type === 'single' ? (
                            /* ── SINGLE COMPANY CALCULATOR ── */
                            <div className="card shadow-sm" style={{ padding: '2rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                                    <div style={{ background: '#10b981', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1rem' }}>📊</div>
                                    <div>
                                        <h3 style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem' }}>CSR Obligation Calculator</h3>
                                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--muted)' }}>Calculate your CSR obligation under Section 135 of the Companies Act, 2013</p>
                                    </div>
                                </div>

                                <div style={{ background: 'var(--secondary)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '1.5rem', overflowX: 'auto' }}>
                                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                            <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Reason for Applicability</label>
                                            <select value={singleCalc.reason} onChange={e => setSingleCalc({ ...singleCalc, reason: e.target.value })} style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', background: 'white' }}>
                                                <option value="Net profit of Rs. 5 crore or more">Net profit of Rs. 5 crore or more</option>
                                                <option value="Net worth of Rs. 500 crore or more">Net worth of Rs. 500 crore or more</option>
                                                <option value="Turnover of Rs. 1000 crore or more">Turnover of Rs. 1000 crore or more</option>
                                            </select>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '200px' }}>
                                            <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Financial Year</label>
                                            <select value={singleCalc.financial_year} onChange={e => {
                                                const fy = e.target.value;
                                                setSingleCalc({ ...singleCalc, financial_year: fy, yearLabels: getPrecedingYears(fy) });
                                            }} style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', background: 'white' }}>
                                                {FINANCIAL_YEARS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
                                        <table className="table" style={{ width: '100%', minWidth: '700px', margin: 0, borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f9fafb', borderBottom: '2px solid var(--border)' }}>
                                                    <th style={{ width: '35%', padding: '1.25rem', color: '#4b5563', fontWeight: 600, fontSize: '0.9rem', textAlign: 'left' }}>Particulars</th>
                                                    {singleCalc.yearLabels.map((y, i) => (
                                                        <th key={i} style={{ textAlign: 'center', padding: '1.25rem' }}>
                                                            <input type="text" value={y} onChange={e => {
                                                                const newLabels = [...singleCalc.yearLabels];
                                                                newLabels[i] = e.target.value;
                                                                setSingleCalc({ ...singleCalc, yearLabels: newLabels });
                                                            }} style={{ width: '100%', textAlign: 'center', background: 'transparent', border: 'none', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#111827', padding: '0.5rem' }} />
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                    <td style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Profit Before Tax (PBT)</td>
                                                    {[0, 1, 2].map(i => (
                                                        <td key={i} style={{ padding: '1rem' }}>
                                                            <input type="number" placeholder="0.00" value={singleCalc.years[i].pbt} onChange={e => {
                                                                const newYears = { ...singleCalc.years, [i]: { ...singleCalc.years[i], pbt: e.target.value } };
                                                                setSingleCalc({ ...singleCalc, years: newYears });
                                                            }} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                    <td style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Net Profit u/s 198</td>
                                                    {[0, 1, 2].map(i => (
                                                        <td key={i} style={{ padding: '1rem' }}>
                                                            <input type="number" placeholder="0.00" value={singleCalc.years[i].netProfit198} onChange={e => {
                                                                const newYears = { ...singleCalc.years, [i]: { ...singleCalc.years[i], netProfit198: e.target.value } };
                                                                setSingleCalc({ ...singleCalc, years: newYears });
                                                            }} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                    <td style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Total amount adjusted as per rule2(1)(h) of the CSR Policy Rules, 2014</td>
                                                    {[0, 1, 2].map(i => (
                                                        <td key={i} style={{ padding: '1rem' }}>
                                                            <input type="number" placeholder="0.00" value={singleCalc.years[i].adjustedAmount} onChange={e => {
                                                                const newYears = { ...singleCalc.years, [i]: { ...singleCalc.years[i], adjustedAmount: e.target.value } };
                                                                setSingleCalc({ ...singleCalc, years: newYears });
                                                            }} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
                                                        </td>
                                                    ))}
                                                </tr>
                                                {(() => {
                                                    const res = computeExcelCSR(singleCalc);
                                                    return (
                                                        <>
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td style={{ padding: '1.25rem', color: '#374151', fontWeight: 600, fontSize: '0.9rem' }}>Total Net Profit for Section-135 (₹)</td>
                                                                {res.tnp.map((val, i) => {
                                                                    const valRupees = val * (val > 0 && val < 10000 ? 10000000 : 1);
                                                                    return <td key={i} style={{ textAlign: 'right', padding: '1.25rem', color: '#111827', fontWeight: 600 }}>{currencyFormatter.format(valRupees)}</td>;
                                                                })}
                                                            </tr>
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td colSpan={3} style={{ padding: '1.25rem', color: '#374151', fontWeight: 600, fontSize: '0.9rem' }}>Average Profit for the three years (₹)</td>
                                                                <td style={{ textAlign: 'right', padding: '1.25rem', color: '#111827', fontWeight: 600 }}>
                                                                    {currencyFormatter.format(res.averageProfitRupees)}
                                                                </td>
                                                            </tr>
                                                            <tr style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
                                                                <td colSpan={3} style={{ padding: '1.25rem', color: '#1e3a8a', fontWeight: 700, fontSize: '0.9rem' }}>CSR for the current year (2% of Average profit) (₹)</td>
                                                                <td style={{ textAlign: 'right', padding: '1.25rem', color: '#1e3a8a', fontWeight: 700 }}>
                                                                    {currencyFormatter.format(res.csrCurrentYearRupees)}
                                                                </td>
                                                            </tr>
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td colSpan={3} style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Set-off Claimed (₹)</td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <input type="number" placeholder="0.00" value={singleCalc.setOffClaimed} onChange={e => setSingleCalc({ ...singleCalc, setOffClaimed: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'right' }} />
                                                                </td>
                                                            </tr>
                                                            <tr style={{ background: '#d1fae5', borderTop: '2px solid #34d399' }}>
                                                                <td colSpan={3} style={{ padding: '1.5rem', color: '#065f46', fontWeight: 800, fontSize: '1.1rem' }}>Final CSR Obligation (₹)</td>
                                                                <td style={{ textAlign: 'right', padding: '1.5rem', color: '#065f46', fontWeight: 800, fontSize: '1.1rem' }}>
                                                                    {currencyFormatter.format(res.finalObligationRupees)}
                                                                </td>
                                                            </tr>
                                                        </>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, background: '#fff', padding: '1.25rem', borderRadius: '8px', border: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text)', fontWeight: 500 }}>Total Amount to be spent on projects (95%)</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#047857' }}>{currencyFormatter.format(computeExcelCSR(singleCalc).projectAmountRupees)}</div>
                                        </div>
                                        <div style={{ flex: 1, background: '#fff', padding: '1.25rem', borderRadius: '8px', border: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text)', fontWeight: 500 }}>Administrative Overhead (5%)</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>{currencyFormatter.format(computeExcelCSR(singleCalc).adminAmountRupees)}</div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginTop: '2rem' }}>
                                        <button
                                            className="btn btn-primary"
                                            disabled={savingCalc || computeExcelCSR(singleCalc).finalObligation <= 0}
                                            style={{ padding: '0.85rem 2.5rem', borderRadius: '10px', fontSize: '1rem', fontWeight: 600, width: '100%' }}
                                            onClick={async () => {
                                                setSavingCalc(true);
                                                try {
                                                    const res = computeExcelCSR(singleCalc);
                                                    const obligationInRupees = Math.round(res.finalObligationRupees);
                                                    const projectBudget = Math.round(res.projectAmountRupees);
                                                    const adminBudget = Math.round(res.adminAmountRupees);
                                                    
                                                    const updateData: any = {
                                                        csr_mandate: obligationInRupees,
                                                        admin_budget: adminBudget,
                                                        project_budget: projectBudget
                                                    };
                                                    
                                                    const { error } = await supabase.from('companies').update(updateData).eq('id', companyId);
                                                    if (error) throw error;
                                                    setCompanyCsrMandate(obligationInRupees);
                                                    setPrimaryAdminBudgetInput(adminBudget.toString());
                                                    setProjectBudgetInput(projectBudget.toString());
                                                    setCsrNotification({ type: 'success', message: `CSR Mandate set to ${currencyFormatter.format(obligationInRupees)}` });
                                                    setTimeout(() => setCsrNotification(null), 4000);
                                                } catch (err: any) {
                                                    setError(err.message);
                                                } finally {
                                                    setSavingCalc(false);
                                                }
                                            }}
                                        >
                                            {savingCalc ? 'Saving...' : '✓ Save as CSR Mandate'}
                                        </button>
                                    </div>
                                </div>

                                <div style={{ marginTop: '1.5rem', padding: '1rem', borderLeft: '4px solid #f59e0b', background: '#fffbeb', borderRadius: '0 8px 8px 0' }}>
                                    <p style={{ color: '#92400e', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                                        <strong>Disclaimer:</strong> This calculation is based on Section 135 of the Indian Companies Act, 2013.
                                        Please verify with your Chartered Accountant (CA) or legal counsel for the actual statutory obligation.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            /* ── GROUP SETUP: CALCULATOR + ADD ENTITY ── */
                            <>
                                {/* First-Time User Guide Banner */}
                                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1d4ed8', fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.4rem' }}>
                                        <span>💡</span> First-Time User Guide: How CSR Calculator & Group Entities & Budgets Work
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#1e40af', lineHeight: '1.5' }}>
                                        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                                            <li style={{ marginBottom: '0.25rem' }}>
                                                <strong>1. Enter Company Financial Details:</strong> Fill in Company Name, Financial Year, Net Worth, Turnover, Net Profit, and Logo.
                                            </li>
                                            <li style={{ marginBottom: '0.25rem' }}>
                                                <strong>2. Check Reason for Eligibility & Fill Numbers:</strong> See the identified CSR Eligibility Reason under Section 135. Fill in the 3 preceding years' PBT & Net Profit numbers to compute the CSR Obligation.
                                            </li>
                                            <li style={{ marginBottom: '0.25rem' }}>
                                                <strong>3. Save to Group Entities & Budgets:</strong> Hitting <strong>Save Company</strong> automatically sets this obligation as the company's <strong>CSR Mandate</strong> in the <strong>Group Entities & Budgets</strong> tab (95% Direct Company Budget, 5% Admin Budget).
                                            </li>
                                            <li>
                                                <strong>4. Foundation Account Spending:</strong> If you wish to spend through your Foundation/Trust (Indirect Contribution), switch to the <strong>Group Entities & Budgets</strong> tab and click <strong>Edit</strong> to adjust budget into <em>Foundation Contr.</em>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="card shadow-sm" style={{ padding: '2rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                                        <div style={{ background: '#10b981', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1rem' }}>📊</div>
                                        <div>
                                            <h3 style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem' }}>{editingEntityId ? 'Edit Group Entity' : 'Add Group Entity via CSR Calculator'}</h3>
                                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--muted)' }}>Enter financial details to calculate CSR obligation, then save as a group entity</p>
                                        </div>
                                    </div>

                                    <form onSubmit={handleSaveEntity}>
                                        <div style={{ background: 'var(--secondary)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '1.25rem' }}>
                                            <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text)' }}>Company Details</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Entity Name <span style={{ color: '#ef4444' }}>*</span></label>
                                                    <input type="text" value={newEntity.name} onChange={e => setNewEntity({ ...newEntity, name: e.target.value })} placeholder="e.g. ABC Industries Ltd or Escrow Account" required style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <input type="checkbox" id="is_escrow" checked={newEntity.is_escrow} onChange={e => setNewEntity({ ...newEntity, is_escrow: e.target.checked })} style={{ width: '1.25rem', height: '1.25rem' }} />
                                                    <label htmlFor="is_escrow" style={{ fontSize: '0.875rem', fontWeight: 500, margin: 0, cursor: 'pointer' }}>Is this an Escrow Account?</label>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Financial Year <span style={{ color: '#ef4444' }}>*</span></label>
                                                    <select required value={newEntity.financial_year} onChange={e => setNewEntity({ ...newEntity, financial_year: e.target.value })} style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', background: 'white', height: '44px' }}>
                                                        {FINANCIAL_YEARS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                                                    </select>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Net Worth (Cr)</label>
                                                    <input 
                                                        type="number" 
                                                        placeholder="e.g. 500" 
                                                        value={newEntity.net_worth_crore} 
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            const nw = Number(val) || 0;
                                                            const to = Number(newEntity.turnover_crore) || 0;
                                                            const np = Number(newEntity.net_profit_crore) || 0;
                                                            let autoReason = entityCalc.reason;
                                                            if (np >= 5) autoReason = 'Net profit of Rs. 5 crore or more';
                                                            else if (nw >= 500) autoReason = 'Net worth of Rs. 500 crore or more';
                                                            else if (to >= 1000) autoReason = 'Turnover of Rs. 1000 crore or more';
                                                            setEntityCalc(prev => ({ ...prev, reason: autoReason }));
                                                            setNewEntity({ ...newEntity, net_worth_crore: val });
                                                        }} 
                                                        style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', height: '44px' }} 
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Turnover (Cr)</label>
                                                    <input 
                                                        type="number" 
                                                        placeholder="e.g. 1000" 
                                                        value={newEntity.turnover_crore} 
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            const nw = Number(newEntity.net_worth_crore) || 0;
                                                            const to = Number(val) || 0;
                                                            const np = Number(newEntity.net_profit_crore) || 0;
                                                            let autoReason = entityCalc.reason;
                                                            if (np >= 5) autoReason = 'Net profit of Rs. 5 crore or more';
                                                            else if (nw >= 500) autoReason = 'Net worth of Rs. 500 crore or more';
                                                            else if (to >= 1000) autoReason = 'Turnover of Rs. 1000 crore or more';
                                                            setEntityCalc(prev => ({ ...prev, reason: autoReason }));
                                                            setNewEntity({ ...newEntity, turnover_crore: val });
                                                        }} 
                                                        style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', height: '44px' }} 
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Net Profit (Cr)</label>
                                                    <input 
                                                        type="number" 
                                                        placeholder="e.g. 5" 
                                                        value={newEntity.net_profit_crore} 
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            const nw = Number(newEntity.net_worth_crore) || 0;
                                                            const to = Number(newEntity.turnover_crore) || 0;
                                                            const np = Number(val) || 0;
                                                            let autoReason = entityCalc.reason;
                                                            if (np >= 5) autoReason = 'Net profit of Rs. 5 crore or more';
                                                            else if (nw >= 500) autoReason = 'Net worth of Rs. 500 crore or more';
                                                            else if (to >= 1000) autoReason = 'Turnover of Rs. 1000 crore or more';
                                                            setEntityCalc(prev => ({ ...prev, reason: autoReason }));
                                                            setNewEntity({ ...newEntity, net_profit_crore: val });
                                                        }} 
                                                        style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', height: '44px' }} 
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label style={{ fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem', fontWeight: 600 }}>Company Logo <span style={{ color: '#ef4444' }}>*</span></label>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                        {getLogoPreviewUrl() ? (
                                                            <div style={{ position: 'relative', width: '48px', height: '48px', flexShrink: 0 }}>
                                                                <img
                                                                    src={getLogoPreviewUrl()!}
                                                                    alt="Company Logo Preview"
                                                                    style={{
                                                                        width: '48px',
                                                                        height: '48px',
                                                                        borderRadius: '10px',
                                                                        objectFit: 'contain',
                                                                        border: '1.5px solid #2563eb',
                                                                        background: '#ffffff',
                                                                        padding: '3px',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                width: '48px',
                                                                height: '48px',
                                                                borderRadius: '10px',
                                                                border: '1.5px dashed #cbd5e1',
                                                                background: '#f8fafc',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '1.2rem',
                                                                flexShrink: 0
                                                            }} title="No logo uploaded">
                                                                🖼️
                                                            </div>
                                                        )}
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                            <input 
                                                                type="file" 
                                                                id="calc-logo-upload" 
                                                                onChange={e => setEntityLogo(e.target.files?.[0] || null)} 
                                                                accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp" 
                                                                style={{ display: 'none' }} 
                                                            />
                                                            <label 
                                                                htmlFor="calc-logo-upload" 
                                                                className="btn btn-sm btn-outline" 
                                                                style={{ 
                                                                    cursor: 'pointer', 
                                                                    margin: 0, 
                                                                    padding: '0.45rem 0.85rem', 
                                                                    fontSize: '0.75rem', 
                                                                    fontWeight: 600,
                                                                    borderRadius: '6px',
                                                                    borderColor: '#cbd5e1',
                                                                    background: '#ffffff',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.35rem'
                                                                }}
                                                            >
                                                                {getLogoPreviewUrl() ? '✓ Change Logo (PNG, JPG)' : '📁 Upload Logo (PNG, JPG)'}
                                                            </label>
                                                            {entityLogo ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEntityLogo(null)}
                                                                    style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.7rem', cursor: 'pointer', padding: 0, textAlign: 'left', fontWeight: 500 }}
                                                                >
                                                                    Remove logo
                                                                </button>
                                                            ) : (
                                                                <span style={{ fontSize: '0.68rem', color: '#64748b' }}>(Supports PNG, JPG, SVG, WEBP)</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Reason for Eligibility Card - Dynamically evaluated based on user inputs */}
                                        {(() => {
                                            const nw = Number(newEntity.net_worth_crore) || 0;
                                            const to = Number(newEntity.turnover_crore) || 0;
                                            const np = Number(newEntity.net_profit_crore) || 0;
                                            const hasEntered = newEntity.net_worth_crore.trim() !== '' || newEntity.turnover_crore.trim() !== '' || newEntity.net_profit_crore.trim() !== '';
                                            
                                            let isEligible = false;
                                            let reasonText = '';
                                            
                                            if (np >= 5) {
                                                isEligible = true;
                                                const npRupees = np * (np < 10000 ? 10000000 : 1);
                                                reasonText = `Net profit of Rs. 5 crore or more (Entered: ${currencyFormatter.format(npRupees)})`;
                                            } else if (nw >= 500) {
                                                isEligible = true;
                                                const nwRupees = nw * (nw < 10000 ? 10000000 : 1);
                                                reasonText = `Net worth of Rs. 500 crore or more (Entered: ${currencyFormatter.format(nwRupees)})`;
                                            } else if (to >= 1000) {
                                                isEligible = true;
                                                const toRupees = to * (to < 10000 ? 10000000 : 1);
                                                reasonText = `Turnover of Rs. 1000 crore or more (Entered: ${currencyFormatter.format(toRupees)})`;
                                            } else if (hasEntered) {
                                                isEligible = false;
                                                reasonText = `Exempt / Below Section 135 Thresholds (Net Profit < ₹5,00,00,000, Net Worth < ₹500,00,00,000, Turnover < ₹1000,00,00,000)`;
                                            } else {
                                                isEligible = false;
                                                reasonText = `Pending Input — Enter Net Worth, Turnover, or Net Profit above to evaluate statutory CSR eligibility`;
                                            }

                                            if (!hasEntered) {
                                                return (
                                                    <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                                            <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
                                                            <strong style={{ color: '#334155', fontSize: '0.95rem' }}>CSR Applicability & Eligibility Status (Section 135)</strong>
                                                        </div>
                                                        <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: '1.5' }}>
                                                            Enter company financial details (Net Worth, Turnover, or Net Profit) above to automatically evaluate statutory eligibility under Section 135 of Companies Act, 2013.
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            if (isEligible) {
                                                return (
                                                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                                            <span style={{ fontSize: '1.2rem', color: '#16a34a' }}>🎯</span>
                                                            <strong style={{ color: '#15803d', fontSize: '0.95rem' }}>CSR Applicability & Eligibility Status (Section 135)</strong>
                                                        </div>
                                                        <div style={{ fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.4rem' }}>
                                                            Reason for Eligibility: <span style={{ background: '#dcfce7', padding: '0.25rem 0.65rem', borderRadius: '6px', border: '1px solid #86efac', color: '#15803d', fontWeight: 700 }}>{reasonText}</span>
                                                        </div>
                                                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#15803d', lineHeight: '1.4' }}>
                                                            Based on Section 135 criteria, enter the 3-year PBT & Net Profit numbers below. When saved, the calculated CSR Obligation is sent automatically to the <strong>Group Entities & Budgets</strong> tab as the statutory CSR Mandate.
                                                        </p>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                                        <span style={{ fontSize: '1.2rem', color: '#d97706' }}>⚠️</span>
                                                        <strong style={{ color: '#92400e', fontSize: '0.95rem' }}>CSR Applicability & Eligibility Status (Section 135)</strong>
                                                    </div>
                                                    <div style={{ fontSize: '0.88rem', color: '#b45309', fontWeight: 600, marginBottom: '0.4rem' }}>
                                                        Status: <span style={{ background: '#fef3c7', padding: '0.25rem 0.65rem', borderRadius: '6px', border: '1px solid #fde68a', color: '#92400e' }}>{reasonText}</span>
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#b45309', lineHeight: '1.4' }}>
                                                        The entered figures do not meet Section 135 mandatory thresholds. You may still calculate and record a voluntary CSR budget below.
                                                    </p>
                                                </div>
                                            );
                                        })()}

                                        <div style={{ background: 'var(--secondary)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '1.25rem', overflowX: 'auto' }}>
                                            <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text)' }}>Financial Data & Calculator</h4>
                                            
                                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                                <div style={{ flex: 1, minWidth: '250px' }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Reason for Applicability</label>
                                                    <select value={entityCalc.reason} onChange={e => setEntityCalc({ ...entityCalc, reason: e.target.value })} style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', background: 'white' }}>
                                                        <option value="Net profit of Rs. 5 crore or more">Net profit of Rs. 5 crore or more</option>
                                                        <option value="Net worth of Rs. 500 crore or more">Net worth of Rs. 500 crore or more</option>
                                                        <option value="Turnover of Rs. 1000 crore or more">Turnover of Rs. 1000 crore or more</option>
                                                    </select>
                                                </div>
                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Financial Year</label>
                                                    <select value={entityCalc.financial_year} onChange={e => {
                                                        const fy = e.target.value;
                                                        setEntityCalc({ ...entityCalc, financial_year: fy, yearLabels: getPrecedingYears(fy) });
                                                    }} style={{ padding: '0.75rem', width: '100%', borderRadius: '8px', border: '1px solid var(--border)', background: 'white' }}>
                                                        {FINANCIAL_YEARS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                                                    </select>
                                                </div>
                                            </div>

                                            <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }}>
                                                <table className="table" style={{ width: '100%', minWidth: '700px', margin: 0, borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid var(--border)' }}>
                                                            <th style={{ width: '35%', padding: '1.25rem', color: '#4b5563', fontWeight: 600, fontSize: '0.9rem', textAlign: 'left' }}>Particulars</th>
                                                            {entityCalc.yearLabels.map((y, i) => (
                                                                <th key={i} style={{ textAlign: 'center', padding: '1.25rem' }}>
                                                                    <input type="text" value={y} onChange={e => {
                                                                        const newLabels = [...entityCalc.yearLabels];
                                                                        newLabels[i] = e.target.value;
                                                                        setEntityCalc({ ...entityCalc, yearLabels: newLabels });
                                                                    }} style={{ width: '100%', textAlign: 'center', background: 'transparent', border: 'none', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#111827', padding: '0.5rem' }} />
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                            <td style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Profit Before Tax (PBT)</td>
                                                            {[0, 1, 2].map(i => (
                                                                <td key={i} style={{ padding: '1rem' }}>
                                                                    <input type="number" placeholder="0.00" value={entityCalc.years[i].pbt} onChange={e => {
                                                                        const newYears = { ...entityCalc.years, [i]: { ...entityCalc.years[i], pbt: e.target.value } };
                                                                        setEntityCalc({ ...entityCalc, years: newYears });
                                                                    }} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
                                                                </td>
                                                            ))}
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                            <td style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Net Profit u/s 198</td>
                                                            {[0, 1, 2].map(i => (
                                                                <td key={i} style={{ padding: '1rem' }}>
                                                                    <input type="number" placeholder="0.00" value={entityCalc.years[i].netProfit198} onChange={e => {
                                                                        const newYears = { ...entityCalc.years, [i]: { ...entityCalc.years[i], netProfit198: e.target.value } };
                                                                        setEntityCalc({ ...entityCalc, years: newYears });
                                                                    }} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
                                                                </td>
                                                            ))}
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                            <td style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Total amount adjusted as per rule2(1)(h) of the CSR Policy Rules, 2014</td>
                                                            {[0, 1, 2].map(i => (
                                                                <td key={i} style={{ padding: '1rem' }}>
                                                                    <input type="number" placeholder="0.00" value={entityCalc.years[i].adjustedAmount} onChange={e => {
                                                                        const newYears = { ...entityCalc.years, [i]: { ...entityCalc.years[i], adjustedAmount: e.target.value } };
                                                                        setEntityCalc({ ...entityCalc, years: newYears });
                                                                    }} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
                                                                </td>
                                                            ))}
                                                        </tr>
                                                {(() => {
                                                    const res = computeExcelCSR(entityCalc);
                                                    return (
                                                        <>
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td style={{ padding: '1.25rem', color: '#374151', fontWeight: 600, fontSize: '0.9rem' }}>Total Net Profit for Section-135 (₹)</td>
                                                                {res.tnp.map((val, i) => {
                                                                    const valRupees = val * (val > 0 && val < 10000 ? 10000000 : 1);
                                                                    return <td key={i} style={{ textAlign: 'right', padding: '1.25rem', color: '#111827', fontWeight: 600 }}>{currencyFormatter.format(valRupees)}</td>;
                                                                })}
                                                            </tr>
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td colSpan={3} style={{ padding: '1.25rem', color: '#374151', fontWeight: 600, fontSize: '0.9rem' }}>Average Profit for the three years (₹)</td>
                                                                <td style={{ textAlign: 'right', padding: '1.25rem', color: '#111827', fontWeight: 600 }}>
                                                                    {currencyFormatter.format(res.averageProfitRupees)}
                                                                </td>
                                                            </tr>
                                                            <tr style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
                                                                <td colSpan={3} style={{ padding: '1.25rem', color: '#1e3a8a', fontWeight: 700, fontSize: '0.9rem' }}>CSR for the current year (2% of Average profit) (₹)</td>
                                                                <td style={{ textAlign: 'right', padding: '1.25rem', color: '#1e3a8a', fontWeight: 700 }}>
                                                                    {currencyFormatter.format(res.csrCurrentYearRupees)}
                                                                </td>
                                                            </tr>
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td colSpan={3} style={{ padding: '1.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Set-off Claimed (₹)</td>
                                                                <td style={{ padding: '1rem' }}>
                                                                    <input type="number" placeholder="0.00" value={entityCalc.setOffClaimed} onChange={e => setEntityCalc({ ...entityCalc, setOffClaimed: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'right' }} />
                                                                </td>
                                                            </tr>
                                                            <tr style={{ background: '#d1fae5', borderTop: '2px solid #34d399' }}>
                                                                <td colSpan={3} style={{ padding: '1.5rem', color: '#065f46', fontWeight: 800, fontSize: '1.1rem' }}>Final CSR Obligation (₹)</td>
                                                                <td style={{ textAlign: 'right', padding: '1.5rem', color: '#065f46', fontWeight: 800, fontSize: '1.1rem' }}>
                                                                    {currencyFormatter.format(res.finalObligationRupees)}
                                                                </td>
                                                            </tr>
                                                        </>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <button
                                        type="button"
                                        className="btn btn-outline"
                                        style={{ padding: '0.6rem 1.5rem', borderRadius: '8px', fontWeight: 500 }}
                                        onClick={() => {
                                            const res = computeExcelCSR(entityCalc);
                                            if (res.isApplicable) {
                                                const obligationInRupees = Math.round(res.finalObligationRupees);
                                                const admin = Math.round(res.adminAmountRupees);
                                                const remaining = Math.round(res.projectAmountRupees);
                                                setNewEntity(prev => ({
                                                    ...prev,
                                                    csr_mandate: obligationInRupees.toString(),
                                                    admin_budget: admin.toString(),
                                                    entity_budget: remaining.toString(),
                                                    primary_account_budget: '0'
                                                }));
                                            }
                                        }}
                                    >
                                        📊 Apply Calculation to Budget
                                    </button>
                                        </div>

                                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                                            <button type="submit" className="btn btn-primary" disabled={entitySaving || (!newEntity.name.trim())} style={{ padding: '0.85rem 2rem', borderRadius: '10px', fontWeight: 600 }}>
                                                {entitySaving ? 'Saving...' : editingEntityId ? '✓ Update Company' : '✓ Save Company'}
                                            </button>
                                            {editingEntityId && <button type="button" className="btn btn-outline" onClick={cancelEdit} style={{ padding: '0.85rem 1.5rem', borderRadius: '10px' }}>Cancel</button>}
                                        </div>
                                    </form>

                                    <div style={{ marginTop: '1.5rem', padding: '1rem', borderLeft: '4px solid #f59e0b', background: '#fffbeb', borderRadius: '0 8px 8px 0' }}>
                                        <p style={{ color: '#92400e', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                                            <strong>Disclaimer:</strong> CSR obligation is calculated as 2% of average net profit under Section 135.
                                            Verify with your CA or legal counsel for statutory compliance.
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'entities' && (
                    <div style={{ display: 'grid', gap: '2rem' }}>
                        {/* First-Time User Guide Banner */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#1e293b', fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem' }}>
                                <span style={{ background: '#e0e7ff', color: '#4f46e5', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>ℹ️</span> 
                                First-Time User Guide: Group Entities & Foundation Allocation
                            </div>
                            <div style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.6' }}>
                                <p style={{ margin: '0 0 1rem 0' }}>
                                    Group Entities added or calculated in the <strong>CSR Calculator</strong> automatically show their <strong>CSR Obligation as Mandate</strong> below.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                                    <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '10px', border: '1px solid #f1f5f9', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <strong style={{ color: '#10b981', display: 'block', marginBottom: '0.25rem' }}>Total CSR Mandate (₹)</strong>
                                        <p style={{ margin: '0', fontSize: '0.8rem', color: '#64748b' }}>Total statutory 2% obligation.</p>
                                    </div>
                                    <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '10px', border: '1px solid #f1f5f9', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <strong style={{ color: '#3b82f6', display: 'block', marginBottom: '0.25rem' }}>Company Budget (Direct)</strong>
                                        <p style={{ margin: '0', fontSize: '0.8rem', color: '#64748b' }}>Funds executed directly by entity.</p>
                                    </div>
                                    <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '10px', border: '1px solid #f1f5f9', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <strong style={{ color: '#8b5cf6', display: 'block', marginBottom: '0.25rem' }}>Foundation Contr. (Indirect)</strong>
                                        <p style={{ margin: '0', fontSize: '0.8rem', color: '#64748b' }}>Funds allocated to Foundation Account.</p>
                                    </div>
                                    <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '10px', border: '1px solid #f1f5f9', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <strong style={{ color: '#f59e0b', display: 'block', marginBottom: '0.25rem' }}>Admin Budget (₹)</strong>
                                        <p style={{ margin: '0', fontSize: '0.8rem', color: '#64748b' }}>Admin overhead budget (5%).</p>
                                    </div>
                                </div>
                                <div style={{ marginTop: '1.25rem', background: '#fffbeb', padding: '1rem', borderRadius: '8px', borderLeft: '4px solid #fcd34d', color: '#92400e', fontSize: '0.85rem', lineHeight: '1.5' }}>
                                    💡 <strong>Spending via Foundation:</strong> By default, CSR Obligation is set to Direct Company Budget. If you want to spend from your Foundation, click <strong>Edit</strong> on any entity below and enter the amount under <strong>Foundation Contr. (Indirect Contr.)</strong> - the Direct Company Budget will auto-adjust!
                                </div>
                            </div>
                        </div>

                        {/* Budget Configuration Subsection */}
                        <div className="card shadow-sm">
                            <h3 style={{ marginBottom: '1.5rem', fontWeight: 600 }}>{companyProfile?.setup_type === 'single' ? 'CSR Finance Details' : 'Group Financial Summary'}</h3>
                            <p style={{ color: 'var(--muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                                Summary of CSR mandate and budget breakdown across all group entities.
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', background: 'transparent', padding: '0.5rem 0', width: '100%' }}>
                                <div style={{ padding: '1.25rem', background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6b7280' }}>Total CSR Mandate (₹)</div>
                                    <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827' }}>
                                        {currencyFormatter.format(companyEntities.filter(e => !e.is_escrow).reduce((sum, e) => sum + (Number(e.csr_mandate) || 0), 0))}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 500 }}>● Auto-calculated total mandate</div>
                                </div>
                                <div style={{ padding: '1.25rem', background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6b7280' }}>Company Budget (Direct)</div>
                                    <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827' }}>
                                        {currencyFormatter.format(companyEntities.filter(e => !e.is_escrow).reduce((sum, e) => sum + (Number(e.entity_budget) || 0), 0))}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 500 }}>● Direct project implementation</div>
                                </div>
                                <div style={{ padding: '1.25rem', background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6b7280' }}>Foundation Contr. (Indirect)</div>
                                    <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827' }}>
                                        {currencyFormatter.format(companyEntities.filter(e => !e.is_escrow).reduce((sum, e) => sum + (Number(e.primary_account_budget) || 0), 0))}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#8b5cf6', fontWeight: 500 }}>● Transferred to Foundation</div>
                                </div>
                                <div style={{ padding: '1.25rem', background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6b7280' }}>Admin Budget (₹)</div>
                                    <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827' }}>
                                        {currencyFormatter.format(companyEntities.filter(e => !e.is_escrow).reduce((sum, e) => sum + (Number(e.admin_budget) || 0), 0))}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 500 }}>● Admin overhead (5%)</div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm">
                            <h3 style={{ marginBottom: '1.5rem', fontWeight: 600, textAlign: 'center' }}>
                                {companyProfile?.setup_type === 'single' 
                                    ? (editingEntityId ? 'Edit Company Budget' : 'Configure Company Budget') 
                                    : (editingEntityId ? 'Edit Group Entity' : 'Add Group Entity')}
                            </h3>
                            {companyProfile?.setup_type === 'single' && companyEntities.filter(e => !e.is_escrow).length > 0 && !editingEntityId && (
                                <div style={{ marginBottom: '1.5rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                                    Your company budget is configured. Click 'Edit' in the table below to modify it.
                                </div>
                            )}
                            <form 
                                onSubmit={handleSaveEntity} 
                                style={{ 
                                    display: (companyProfile?.setup_type === 'single' && companyEntities.filter(e => !e.is_escrow).length > 0 && !editingEntityId) ? 'none' : 'flex',
                                    gap: '1rem', alignItems: 'flex-end', background: 'var(--secondary)', padding: '1rem', borderRadius: '12px', marginBottom: '2rem', flexWrap: 'wrap', border: '1px solid var(--border)', width: '100%' 
                                }}
                            >
                                    {companyProfile?.setup_type !== 'single' && (
                                        <div className="form-group" style={{ flex: 2, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                            <label style={{ fontSize: '0.875rem' }}>Company Name *</label>
                                            <input type="text" value={newEntity.name} onChange={e => setNewEntity({ ...newEntity, name: e.target.value })} placeholder="Company Name" required style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
                                        </div>
                                    )}
                                    <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem' }}>Total CSR Mandate (₹)</label>
                                        <input 
                                            type="number" 
                                            value={newEntity.csr_mandate} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                const total = Number(val) || 0;
                                                const admin = Math.round(total * 0.05);
                                                const project = total - admin;
                                                setNewEntity({ 
                                                    ...newEntity, 
                                                    csr_mandate: val,
                                                    admin_budget: admin.toString(),
                                                    entity_budget: project.toString(),
                                                    primary_account_budget: '0'
                                                });
                                            }} 
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} 
                                        />
                                    </div>
                                    <div className="form-group" style={{ flex: 1.2, minWidth: 'min(100%, 180px)', marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem' }}>Company Budget (Direct Contr.)</label>
                                        <input 
                                            type="number" 
                                            value={newEntity.entity_budget} 
                                            onChange={e => setNewEntity({ ...newEntity, entity_budget: e.target.value })}
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} 
                                        />
                                    </div>
                                    {companyProfile?.setup_type !== 'single' && (
                                        <div className="form-group" style={{ flex: 1.2, minWidth: 'min(100%, 180px)', marginBottom: 0 }}>
                                            <label style={{ fontSize: '0.875rem' }}>Foundation Contr. (Indirect Contr.)</label>
                                            <input 
                                                type="number" 
                                                value={newEntity.primary_account_budget} 
                                                onChange={e => {
                                                    const fndVal = e.target.value;
                                                    const total = Number(newEntity.csr_mandate) || 0;
                                                    const admin = Number(newEntity.admin_budget) || 0;
                                                    const fnd = Number(fndVal) || 0;
                                                    const remaining = Math.max(0, total - admin - fnd);
                                                    setNewEntity({ 
                                                        ...newEntity, 
                                                        primary_account_budget: fndVal,
                                                        entity_budget: remaining.toString()
                                                    });
                                                }} 
                                                style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} 
                                            />
                                        </div>
                                    )}
                                    <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem' }}>Admin Budget (₹)</label>
                                        <input type="number" value={newEntity.admin_budget} onChange={e => setNewEntity({ ...newEntity, admin_budget: e.target.value })} style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
                                    </div>
                                    {companyProfile?.setup_type !== 'single' && (
                                        <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                            <label style={{ fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Logo Preview</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                {getLogoPreviewUrl() ? (
                                                    <img
                                                        src={getLogoPreviewUrl()!}
                                                        alt="Logo Preview"
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '6px',
                                                            objectFit: 'contain',
                                                            border: '1px solid var(--border)',
                                                            background: '#ffffff',
                                                            padding: '1px'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        borderRadius: '6px',
                                                        border: '1px dashed var(--border)',
                                                        background: '#ffffff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'var(--muted)',
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        None
                                                    </div>
                                                )}
                                                <input type="file" id="logo-upload" onChange={e => setEntityLogo(e.target.files?.[0] || null)} accept="image/*" style={{ display: 'none' }} />
                                                <label htmlFor="logo-upload" className="btn btn-sm btn-outline" style={{ cursor: 'pointer', margin: 0, padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>
                                                    {getLogoPreviewUrl() ? 'Change' : 'Upload'}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button type="submit" className="btn btn-primary" disabled={entitySaving} style={{ padding: '0.6rem 1.5rem', borderRadius: '6px' }}>
                                            {companyProfile?.setup_type === 'single' ? 'Save Budget' : (editingEntityId ? 'Update' : 'Add')}
                                        </button>
                                        {editingEntityId && <button type="button" className="btn btn-outline" onClick={cancelEdit}>Cancel</button>}
                                    </div>
                                </form>

                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '60px' }}>Logo</th>
                                                <th>Company Name</th>
                                                <th>Total CSR Mandate (₹)</th>
                                                <th>Company Budget (Direct)</th>
                                                {companyProfile?.setup_type !== 'single' && <th>Foundation Contr. (Indirect)</th>}
                                                <th>Admin Budget</th>
                                                <th style={{ textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {companyEntities.filter(e => !e.is_escrow).length === 0 ? (
                                                companyProfile?.setup_type === 'single' ? (
                                                    <tr key="virtual">
                                                        <td>
                                                            {(companyProfile.logo_url) && !logoErrors['mock'] ? (
                                                                <img src={companyProfile.logo_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'contain', border: '1px solid var(--border)' }} onError={() => setLogoErrors(prev => ({ ...prev, mock: true }))} />
                                                            ) : (
                                                                <div style={{ width: '40px', height: '40px', borderRadius: '4px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                                                                    {(companyProfile.name || companyProfile.company_name || 'C').charAt(0)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ fontWeight: 600 }}>{companyProfile.name || companyProfile.company_name}</td>
                                                        <td style={{ fontWeight: 700, color: '#047857' }}>{currencyFormatter.format(companyProfile.csr_mandate || 0)}</td>
                                                        <td>{currencyFormatter.format((companyProfile.csr_mandate || 0) - (companyProfile.admin_budget || 0))}</td>
                                                        <td>{currencyFormatter.format(companyProfile.admin_budget || 0)}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <button className="btn btn-sm btn-outline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Configure Budget</button>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No group entities added yet.</td></tr>
                                                )
                                            ) : (
                                                companyEntities.filter(e => !e.is_escrow).map(entity => (
                                                    <tr key={entity.id}>
                                                        <td>
                                                            {(signedLogoUrls[entity.logo_url] || entity.logo_url) && !logoErrors[entity.id] ? (
                                                                <img 
                                                                    src={signedLogoUrls[entity.logo_url] || entity.logo_url} 
                                                                    alt="" 
                                                                    style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'contain', border: '1px solid var(--border)' }} 
                                                                    onError={() => setLogoErrors(prev => ({ ...prev, [entity.id]: true }))}
                                                                />
                                                            ) : (
                                                                <div style={{ width: '40px', height: '40px', borderRadius: '4px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                                                                    {entity.name?.charAt(0) || 'C'}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ fontWeight: 600 }}>{entity.name}</td>
                                                        <td style={{ fontWeight: 700, color: '#047857' }}>{currencyFormatter.format(entity.csr_mandate || 0)}</td>
                                                        <td>{currencyFormatter.format(entity.entity_budget || 0)}</td>
                                                        {companyProfile?.setup_type !== 'single' && <td>{currencyFormatter.format(entity.primary_account_budget || 0)}</td>}
                                                        <td>{currencyFormatter.format(entity.admin_budget || 0)}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <button className="btn btn-sm btn-outline" onClick={() => handleEditClick(entity)}>Edit</button>
                                                            {companyProfile?.setup_type !== 'single' && (
                                                                <button className="btn btn-sm btn-outline" style={{ color: 'var(--error)', marginLeft: '0.5rem' }} onClick={() => handleDeleteEntity(entity.id)}>Remove</button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                

                            </div>
                    </div>
                )}

                {/* ══════════════════════ ESCROW ACCOUNTS TAB ══════════════════════ */}
                {activeTab === 'escrow' && (
                    <div style={{ display: 'grid', gap: '2rem' }}>
                        <div className="card shadow-sm">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div style={{ background: '#db2777', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1rem' }}>E</div>
                                <div>
                                    <h3 style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem' }}>Escrow Accounts (Unspent CSR)</h3>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--muted)' }}>Manage your Escrow Accounts for unspent CSR obligations</p>
                                </div>
                            </div>
                            
                            <h3 style={{ marginBottom: '1.5rem', fontWeight: 600 }}>{editingEntityId ? 'Edit Escrow Account' : 'Add Escrow Account'}</h3>
                            <form onSubmit={(e) => {
                                // Force is_escrow true when saving from this tab
                                e.preventDefault();
                                // We inject is_escrow: true in handleSaveEntity manually or simply patch the state right here before submit
                                setNewEntity(prev => ({ ...prev, is_escrow: true }));
                                handleSaveEntity(e);
                            }} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', background: '#fdf2f8', padding: '1rem', borderRadius: '12px', marginBottom: '2rem', flexWrap: 'wrap', border: '1px solid #fbcfe8', width: '100%' }}>
                                <div className="form-group" style={{ flex: 2, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Account / Bank Name *</label>
                                    <input type="text" value={newEntity.name} onChange={e => setNewEntity({ ...newEntity, name: e.target.value, is_escrow: true })} placeholder="e.g. CSR Unspent Escrow 2024" required style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
                                </div>
                                <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Unspent Amount from Last Year (₹)</label>
                                    <input 
                                        type="number" 
                                        value={newEntity.csr_mandate} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            setNewEntity({ 
                                                ...newEntity, 
                                                csr_mandate: val,
                                                entity_budget: val, // Put everything in direct for escrow by default
                                                admin_budget: '0',
                                                primary_account_budget: '0',
                                                is_escrow: true
                                            });
                                        }} 
                                        style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} 
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Logo Preview</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {getLogoPreviewUrl() ? (
                                            <img
                                                src={getLogoPreviewUrl()!}
                                                alt="Logo Preview"
                                                style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'contain', border: '1px solid var(--border)', background: '#ffffff', padding: '1px' }}
                                            />
                                        ) : (
                                            <div style={{ width: '36px', height: '36px', borderRadius: '6px', border: '1px dashed var(--border)', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                                None
                                            </div>
                                        )}
                                        <input type="file" id="escrow-logo-upload" onChange={e => setEntityLogo(e.target.files?.[0] || null)} accept="image/*" style={{ display: 'none' }} />
                                        <label htmlFor="escrow-logo-upload" className="btn btn-sm btn-outline" style={{ cursor: 'pointer', margin: 0, padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>
                                            {getLogoPreviewUrl() ? 'Change' : 'Upload'}
                                        </label>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button type="submit" className="btn btn-primary" disabled={entitySaving} style={{ padding: '0.6rem 1.5rem', borderRadius: '6px', backgroundColor: '#db2777', color: 'white' }}>
                                        {editingEntityId ? 'Update' : 'Add Escrow'}
                                    </button>
                                    {editingEntityId && <button type="button" className="btn btn-outline" onClick={cancelEdit}>Cancel</button>}
                                </div>
                            </form>

                            <div className="table-container">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>Logo</th>
                                            <th>Account Name</th>
                                            <th>Total Balance (₹)</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {companyEntities.filter(e => e.is_escrow).length === 0 ? (
                                            <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No escrow accounts added yet.</td></tr>
                                        ) : (
                                            companyEntities.filter(e => e.is_escrow).map(entity => (
                                                <tr key={entity.id}>
                                                    <td>
                                                        {(signedLogoUrls[entity.logo_url] || entity.logo_url) && !logoErrors[entity.id] ? (
                                                            <img 
                                                                src={signedLogoUrls[entity.logo_url] || entity.logo_url} 
                                                                alt="" 
                                                                style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'contain', border: '1px solid var(--border)' }} 
                                                                onError={() => setLogoErrors(prev => ({ ...prev, [entity.id]: true }))}
                                                            />
                                                        ) : (
                                                            <div style={{ width: '40px', height: '40px', borderRadius: '4px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                                                                {entity.name?.charAt(0) || 'E'}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>{entity.name} <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', backgroundColor: '#fce7f3', color: '#be185d', borderRadius: '4px', marginLeft: '0.5rem' }}>Escrow</span></td>
                                                    <td style={{ fontWeight: 700, color: '#047857' }}>{currencyFormatter.format(entity.csr_mandate || 0)}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button className="btn btn-sm btn-outline" onClick={() => handleEditClick(entity)}>Edit</button>
                                                        <button className="btn btn-sm btn-outline" style={{ color: 'var(--error)', marginLeft: '0.5rem' }} onClick={() => handleDeleteEntity(entity.id)}>Remove</button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'admin' && (
                    <section className="card shadow-sm">
                        <h3 style={{ fontWeight: 600 }}>Administrative Expenses</h3>
                        <p style={{ color: 'var(--muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                            Track overhead costs, salaries, and general company travel expenses. According to CSR rules, this should normally be capped at 5% of your total CSR expenditure.
                        </p>
                        <form onSubmit={handleSaveAdminExpense} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', background: 'var(--secondary)', padding: '1rem', borderRadius: '12px', marginBottom: '2rem', flexWrap: 'wrap', border: '1px solid var(--border)', width: '100%' }}>
                            <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                <label style={{ fontSize: '0.875rem' }}>Date *</label>
                                <input
                                    type="date"
                                    value={newAdminExpense.date}
                                    onChange={e => setNewAdminExpense({ ...newAdminExpense, date: e.target.value })}
                                    required
                                    style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                />
                            </div>
                            <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                <label style={{ fontSize: '0.875rem' }}>Category *</label>
                                <select
                                    value={newAdminExpense.category}
                                    onChange={e => setNewAdminExpense({ ...newAdminExpense, category: e.target.value })}
                                    required
                                    style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)', height: '40px', background: 'white' }}
                                >
                                    <option value="Travel">Travel</option>
                                    <option value="Salaries">Salaries</option>
                                    <option value="Office/Rent">Office / Rent</option>
                                    <option value="Software/IT">Software / IT</option>
                                    <option value="Other">Other Overhead</option>
                                </select>
                            </div>
                            {companyProfile?.setup_type !== 'single' && (
                                <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Company *</label>
                                    <select
                                        value={newAdminExpense.entity_id}
                                        onChange={e => setNewAdminExpense({ ...newAdminExpense, entity_id: e.target.value })}
                                        style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)', height: '40px', background: 'white' }}
                                    >
                                        <option value="">Foundation Account</option>
                                        {companyEntities.filter(e => !e.is_escrow).map((entity: any) => (
                                            <option key={entity.id} value={entity.id}>{entity.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                <label style={{ fontSize: '0.875rem' }}>Amount (₹) *</label>
                                <input
                                    type="number"
                                    value={newAdminExpense.amount}
                                    onChange={e => setNewAdminExpense({ ...newAdminExpense, amount: e.target.value })}
                                    min="1"
                                    required
                                    style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                />
                            </div>
                            <div className="form-group" style={{ flex: 2, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                <label style={{ fontSize: '0.875rem' }}>Description</label>
                                <input
                                    type="text"
                                    value={newAdminExpense.description}
                                    onChange={e => setNewAdminExpense({ ...newAdminExpense, description: e.target.value })}
                                    placeholder="e.g. Flight to Delhi"
                                    style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                />
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={isSavingAdminExpense} style={{ height: '40px', padding: '0 1.5rem' }}>
                                {isSavingAdminExpense ? 'Saving...' : 'Add Expense'}
                            </button>
                        </form>

                        <div className="table-container">
                            <table className="table">
                                <thead><tr><th>Date</th><th>Category</th><th>Company</th><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
                                <tbody>
                                    {adminExpenses.length === 0 ? (
                                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No administrative expenses logged yet.</td></tr>
                                    ) : (
                                        adminExpenses.map(exp => (
                                            <tr key={exp.id}>
                                                <td>{new Date(exp.expense_date).toLocaleDateString()}</td>
                                                <td><span style={{ padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: 'white', border: '1px solid var(--border)' }}>{exp.category}</span></td>
                                                <td>
                                                    <span style={{
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.75rem',
                                                        backgroundColor: (exp.company_entities?.name) ? '#3b82f615' : '#10b98115',
                                                        color: (exp.company_entities?.name) ? '#3b82f6' : '#10b981',
                                                        display: 'inline-block'
                                                    }}>
                                                        {exp.company_entities?.name || (companyProfile?.setup_type === 'single' ? companyProfile?.name : 'Foundation Account')}
                                                    </span>
                                                </td>
                                                <td>{exp.description}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>{currencyFormatter.format(exp.amount)}</td>
                                                <td style={{ textAlign: 'right' }}><button className="btn btn-sm btn-outline" style={{ color: 'var(--error)' }} onClick={() => handleDeleteAdminExpense(exp.id)}>Remove</button></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {activeTab === 'transfers' && companyProfile?.setup_type !== 'single' && (
                    <div className="card shadow-sm">
                        <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Foundation Transfers</h3>
                        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Record fund transfers from company entities to the core foundation account.</p>
                        <div id="record-transfer-form" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f5f1f0', borderRadius: '8px', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '1rem' }}>{editingContributionId ? 'Edit Transfer' : 'Record Transfer'}</h4>
                                {editingContributionId && (
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => {
                                            setEditingContributionId(null);
                                            setNewContribution({ date: new Date().toISOString().split('T')[0], amount: '', entity_id: '', notes: '' });
                                        }}
                                        style={{ fontSize: '0.7rem', padding: '0.1rem 0.5rem', marginBottom: '1rem' }}
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                            </div>
                            <form onSubmit={handleSaveFoundationContribution} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
                                <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Date *</label>
                                    <input type="date" value={newContribution.date} onChange={e => setNewContribution({ ...newContribution, date: e.target.value })} required style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
                                </div>
                                <div className="form-group" style={{ flex: 1, minWidth: 'min(100%, 150px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Amount (₹) *</label>
                                    <input type="number" value={newContribution.amount} onChange={e => setNewContribution({ ...newContribution, amount: e.target.value })} required placeholder="0.00" style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
                                </div>
                                <div className="form-group" style={{ flex: 2, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Source Company</label>
                                    <select value={newContribution.entity_id} onChange={e => setNewContribution({ ...newContribution, entity_id: e.target.value })} style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}>
                                        <option value="">Select Company / Source</option>
                                        {companyEntities.map(e => <option key={e.id} value={e.id}>{e.name} {e.is_escrow ? '(Escrow Account)' : ''}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: 2, minWidth: 'min(100%, 200px)', marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.875rem' }}>Notes</label>
                                    <input type="text" value={newContribution.notes} onChange={e => setNewContribution({ ...newContribution, notes: e.target.value })} placeholder="Transfer details..." style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
                                </div>
                                <button type="submit" className="btn btn-primary" disabled={isSavingContribution} style={{ padding: '0.6rem 1.5rem', borderRadius: '6px' }}>
                                    {isSavingContribution ? (editingContributionId ? 'Updating...' : 'Recording...') : (editingContributionId ? 'Update' : 'Record')}
                                </button>
                            </form>
                        </div>
                        <div className="table-container">
                            <table className="table">
                                <thead><tr><th style={{ width: '15%' }}>Date</th><th style={{ width: '25%' }}>Source Company</th><th style={{ width: '35%' }}>Notes</th><th style={{ textAlign: 'right', width: '15%' }}>Amount</th><th style={{ textAlign: 'right', width: '10%' }}>Actions</th></tr></thead>
                                <tbody>
                                    {foundationContributions.length === 0 ? (
                                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No transfers recorded yet.</td></tr>
                                    ) : (
                                        foundationContributions.map(contribution => (
                                            <tr key={contribution.id}>
                                                <td style={{ fontSize: '0.85rem' }}>{new Date(contribution.contribution_date).toLocaleDateString()}</td>
                                                <td>
                                                    <span style={{
                                                        padding: '0.2rem 0.5rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.7rem',
                                                        backgroundColor: '#3b82f615',
                                                        color: '#3b82f6',
                                                        display: 'inline-block',
                                                        maxWidth: '150px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }} title={contribution.company_entities?.name || 'N/A'}>
                                                        {contribution.company_entities?.name || (companyProfile?.setup_type === 'single' ? companyProfile?.name : 'N/A')}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '0.8rem', color: 'var(--muted)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={contribution.notes}>{contribution.notes || '-'}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.9rem' }}>{currencyFormatter.format(contribution.amount)}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                                        <button
                                                            className="btn btn-sm btn-outline"
                                                            style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                                                            onClick={() => handleEditContribution(contribution)}
                                                            title="Edit Record"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline"
                                                            style={{ color: 'var(--error)', borderColor: 'var(--error)', padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                                                            onClick={() => handleDeleteContribution(contribution.id)}
                                                            title="Delete Record"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}


                {activeTab === 'disbursements' && (
                    <div className="card shadow-sm">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                            <div>
                                <h3 style={{ fontWeight: 600 }}>Fund Release History</h3>
                                <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Track all fund releases to NGOs and records of direct implementation expenditures.</p>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {editingDisbursementId && (
                                    <button
                                        className="btn btn-outline"
                                        onClick={() => {
                                            setEditingDisbursementId(null);
                                            setDirectExpData({ project_id: '', entity_id: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
                                            setShowDirectExpForm(false);
                                        }}
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                                <button className="btn btn-primary" onClick={() => setShowDirectExpForm(!showDirectExpForm)}>
                                    {showDirectExpForm ? 'Cancel' : 'Record Direct Expenditure'}
                                </button>
                            </div>
                        </div>

                        {showDirectExpForm && (
                            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bcf0da' }}>
                                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.25rem', color: '#065f46' }}>{editingDisbursementId ? 'Edit Expenditure Record' : 'Record Direct Project Expenditure'}</h4>
                                <form onSubmit={handleSaveDirectExpenditure} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '1.25rem', alignItems: 'flex-end', width: '100%' }}>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Select Project *</label>
                                        <select
                                            value={directExpData.project_id}
                                            onChange={e => setDirectExpData({ ...directExpData, project_id: e.target.value })}
                                            required
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                        >
                                            <option value="">Select Project</option>
                                            {projects.filter(p => p.implementation_type === 'direct').map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                            {companyProfile?.setup_type === 'single' ? 'Source Account' : 'Source Company'}
                                        </label>
                                        <select
                                            value={directExpData.entity_id}
                                            onChange={e => setDirectExpData({ ...directExpData, entity_id: e.target.value })}
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                        >
                                            <option value="">
                                                {companyProfile?.setup_type === 'single' ? 'Main Account' : 'Foundation Account'}
                                            </option>
                                            {companyEntities.map(e => (
                                                <option key={e.id} value={e.id}>{e.name} {e.is_escrow ? '(Escrow Account)' : ''}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Amount (₹) *</label>
                                        <input
                                            type="number"
                                            value={directExpData.amount}
                                            onChange={e => setDirectExpData({ ...directExpData, amount: e.target.value })}
                                            required
                                            placeholder="0.00"
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Date *</label>
                                        <input
                                            type="date"
                                            value={directExpData.date}
                                            onChange={e => setDirectExpData({ ...directExpData, date: e.target.value })}
                                            required
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0, gridColumn: 'span 2' }}>
                                        <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Notes / Description</label>
                                        <input
                                            type="text"
                                            value={directExpData.notes}
                                            onChange={e => setDirectExpData({ ...directExpData, notes: e.target.value })}
                                            placeholder="e.g. Procurement of materials..."
                                            style={{ padding: '0.6rem', width: '100%', borderRadius: '6px', border: '1px solid var(--border)' }}
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-primary" disabled={isSavingDirectExp} style={{ padding: '0.6rem 1.5rem', borderRadius: '6px', height: 'fit-content' }}>
                                        {isSavingDirectExp ? (editingDisbursementId ? 'Updating...' : 'Recording...') : (editingDisbursementId ? 'Update' : 'Record')}
                                    </button>
                                </form>
                            </div>
                        )}
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '20%' }}>PROJECT</th>
                                        <th style={{ width: '15%' }}>NGO / VENDOR</th>
                                        <th style={{ width: '15%' }}>SOURCE COMPANY</th>
                                        <th style={{ textAlign: 'right', width: '12%' }}>AMOUNT</th>
                                        <th style={{ width: '10%' }}>DATE</th>
                                        <th style={{ width: '18%' }}>NOTES</th>
                                        <th style={{ width: '10%', textAlign: 'right' }}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {disbursements.length === 0 ? (
                                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No direct implementation expenditures recorded yet.</td></tr>
                                    ) : (
                                        disbursements.map(disb => (
                                            <tr key={disb.id}>
                                                <td style={{ fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={disb.projects?.name}>{disb.projects?.name}</td>
                                                <td style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>{disb.ngos?.legal_name || 'Direct Implementation'}</td>
                                                <td>
                                                    <span style={{
                                                        padding: '0.2rem 0.5rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.7rem',
                                                        backgroundColor: disb.company_entities?.name ? '#3b82f615' : '#10b98115',
                                                        color: disb.company_entities?.name ? '#3b82f6' : '#10b981',
                                                        display: 'inline-block',
                                                        maxWidth: '120px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }} title={disb.company_entities?.name || (companyProfile?.setup_type === 'single' ? companyProfile?.name : 'Foundation Account')}>
                                                        {disb.company_entities?.name || (companyProfile?.setup_type === 'single' ? companyProfile?.name : 'Foundation Account')}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.9rem' }}>{currencyFormatter.format(disb.amount)}</td>
                                                <td style={{ fontSize: '0.85rem' }}>{new Date(disb.released_at).toLocaleDateString()}</td>
                                                <td style={{ fontSize: '0.8rem', color: 'var(--muted)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={disb.notes}>{disb.notes || '-'}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                                        <button
                                                            className="btn btn-sm btn-outline"
                                                            style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                                                            onClick={() => handleEditDisbursement(disb)}
                                                            title="Edit Record"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline"
                                                            style={{ color: 'var(--error)', borderColor: 'var(--error)', padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                                                            onClick={() => handleDeleteDisbursement(disb.id)}
                                                            title="Delete Record"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CorporateFinancials;
